import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { executeQueryStream, clearChatHistory } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AnalysisStepper from '../components/AnalysisStepper';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const COVERAGE_PROGRESS_STEPS = [
  { id: 'routing', title: 'Classifying query' },
  { id: 'init', title: 'Initializing analysis agent' },
  { id: 'intent', title: 'Understanding query intent' },
  { id: 'entity', title: 'Identifying entities' },
  { id: 'context', title: 'Loading conversation context' },
  { id: 'agent', title: 'Running analysis' },
  { id: 'sql', title: 'Generating SQL' },
  { id: 'sql_exec', title: 'Executing query' },
  { id: 'finalizing', title: 'Preparing results' },
  { id: 'reasoning', title: 'Generating reasoning' },
];

const SUPPORT_PROGRESS_STEPS = [
  { id: 'routing', title: 'Classifying query' },
  { id: 'support', title: 'Searching knowledge base' },
];

function normalizeProgressStep(step) {
  if (step === 'sql_gen' || step === 'sql_retry') return 'sql';
  return step;
}

const QueryPage = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState('');
  const [userIdDraft, setUserIdDraft] = useState('');
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingSteps, setLoadingSteps] = useState(COVERAGE_PROGRESS_STEPS);
  const [loadingActiveId, setLoadingActiveId] = useState('routing');
  const [chatHistory, setChatHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedQueryId, setSelectedQueryId] = useState(null);
  const messagesEndRef = useRef(null);

  

  useEffect(() => {
  // Load persisted user id if available
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem('insightly_user_id') : null;
  if (stored) {
    setUserId(stored);
    setUserIdDraft(stored);
    // Clear chat history on page refresh/load if user_id exists
    clearChatHistory(stored).then(result => {
      if (result.success) {
        console.log(`Chat history cleared for user ${stored}: ${result.message}`);
      } else {
        console.warn(`Failed to clear chat history: ${result.message || result.error}`);
      }
    }).catch(err => {
      console.error('Error clearing chat history on load:', err);
    });
  }
  const handleResize = () => {
    // Force re-render on window resize for responsive behavior
    // The ref width will be checked in the render cycle
  };
  
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, isLoading]);

  

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };


  const handleQuerySubmit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (!userId || !userId.trim()) {
      return;
    }
    
    // Prevent duplicate submissions
    if (isLoading) {
      return;
    }

    // Store query before clearing it (CRITICAL BUG FIX)
    const currentQuery = query.trim();
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentQuery,
      timestamp: new Date().toLocaleTimeString()
    };

    setChatHistory(prev => [...prev, userMessage]);
    setQuery('');
    setIsLoading(true);
    setLoadingSteps(COVERAGE_PROGRESS_STEPS);
    setLoadingActiveId('routing');
    setLoadingMessage('Processing Query');

    try {
      const response = await executeQueryStream(currentQuery, userId, (message, step) => {
        if (step === 'support') {
          setLoadingSteps(SUPPORT_PROGRESS_STEPS);
        }
        setLoadingActiveId(normalizeProgressStep(step));
        setLoadingMessage(message);
      });
      
      // Validate response exists and is an object
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response received from server');
      }
      
      // Safely extract response data with null checks
      let assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: (response.answer || response.content || 'Query executed successfully'),
        timestamp: new Date().toLocaleTimeString(),
        sqlQuery: response.executed_sql_query || response.generated_sql_query || response.sql_query || null,
        results: response.data || undefined,
        insights: response.insights || undefined,
        chartData: response.chart_data || undefined,
        chartExplanation: response.chart_explanation || undefined,
        reasoning: response.reasoning || null
      };

      // Check for error in response
      if (response.error || response.success === false) {
        assistantMessage.content = `Error: ${response.error || response.message || 'Unknown error occurred'}`;
        assistantMessage.error = true;
      }

      // Safely update chat history
      try {
        setChatHistory(prev => [...prev, assistantMessage]);
      } catch (stateError) {
        console.error('Error updating chat history:', stateError);
        throw new Error('Failed to update chat history. State may be corrupted.');
      }
      
      // Tabs removed - no longer setting active tabs
    } catch (err) {
      //clearInterval(stepInterval);
      console.error('Query execution error:', err);
      
      // Determine error message
      let errorContent = 'Failed to execute query';
      if (err.message) {
        errorContent = err.message;
      } else if (err.response?.data?.detail) {
        errorContent = err.response.data.detail;
      } else if (err.response?.data?.error) {
        errorContent = err.response.data.error;
      }
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `Error: ${errorContent}`,
        error: true,
        timestamp: new Date().toLocaleTimeString()
      };
      
      // Safely update chat history
      try {
        setChatHistory(prev => [...prev, errorMessage]);
      } catch (stateError) {
        console.error('Error updating chat history:', stateError);
        // If state update fails, try to recover by resetting
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } finally {
      // Always reset loading state, even if there was an error
      try {
        setIsLoading(false);
        setLoadingMessage('');
      } catch (stateError) {
        console.error('Error resetting loading state:', stateError);
      }
    }
  };

  // No disconnect logic needed; backend does not manage sessions

  const handleNewQuery = async () => {
    // Clear local chat history
    setChatHistory([]);
    setSelectedQueryId(null);
    
    // Clear chat history on backend if user_id exists
    if (userId && userId.trim()) {
      try {
        const result = await clearChatHistory(userId);
        if (result.success) {
          console.log(`Chat history cleared for user ${userId}: ${result.message}`);
        } else {
          console.warn(`Failed to clear chat history: ${result.message || result.error}`);
        }
      } catch (err) {
        console.error('Error clearing chat history:', err);
      }
    }
  };

  const handleSaveUserId = () => {
    const trimmed = (userIdDraft || '').trim();
    setUserId(trimmed);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('insightly_user_id', trimmed);
    }
  };

  const handleClearHistory = async () => {
    // Clear local chat history
    setChatHistory([]);
    setSelectedQueryId(null);
    
    // Clear chat history on backend if user_id exists
    if (userId && userId.trim()) {
      try {
        const result = await clearChatHistory(userId);
        if (result.success) {
          console.log(`Chat history cleared for user ${userId}: ${result.message}`);
        } else {
          console.warn(`Failed to clear chat history: ${result.message || result.error}`);
        }
      } catch (err) {
        console.error('Error clearing chat history:', err);
      }
    }
  };

  const handleChatHistoryClick = (messageId) => {
    setSelectedQueryId(messageId);
    // Scroll to the selected message
    setTimeout(() => {
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const sampleQueries = [
    'What is my coverage on Currys in UK?',
    'Give me Pageview Coverage Trend',
    'Who has the highest coverage among retailers?'
  ];

  const handleSampleQuery = (sampleQuery) => {
    setQuery(sampleQuery);
  };


  const markdownComponents = {
    a: ({ node, children, ...props }) => {
      const className = ['text-blue-600 underline font-semibold break-all', props.className]
        .filter(Boolean)
        .join(' ');
      const isAnchorLink = (props.href || '').startsWith('#');
      return (
        <a
          {...props}
          className={className}
          target={isAnchorLink ? undefined : '_blank'}
          rel={isAnchorLink ? undefined : 'noopener noreferrer'}
        >
          {children}
        </a>
      );
    }
  };

  const renderMessage = (message) => {
    // If a specific query is selected, only show that query and its response
    if (selectedQueryId && message.type === 'user' && message.id !== selectedQueryId) {
      return null;
    }
    if (selectedQueryId && message.type === 'assistant') {
      // Find the user message that corresponds to this assistant message
      if (!Array.isArray(chatHistory)) {
        console.warn('chatHistory is not an array:', chatHistory);
        return null;
      }
      const userMessageIndex = chatHistory.findIndex(msg => msg && msg.id === selectedQueryId && msg.type === 'user');
      const assistantMessageIndex = chatHistory.findIndex(msg => msg && msg.id === message.id);
      
      // Only show the assistant message if it comes after the selected user message
      // and before the next user message (if any)
      if (userMessageIndex !== -1 && assistantMessageIndex > userMessageIndex) {
        const nextUserMessageIndex = chatHistory.findIndex((msg, index) => 
          index > assistantMessageIndex && msg.type === 'user'
        );
        
        // If there's no next user message, or this assistant message comes before it
        if (nextUserMessageIndex === -1 || assistantMessageIndex < nextUserMessageIndex) {
          // Show this assistant message
        } else {
          return null;
        }
      } else {
        return null;
      }
    }

    if (message.type === 'user') {
      return (
        <div key={message.id} id={`message-${message.id}`} className="flex justify-end mb-4">
          <div className="bg-primary-600 text-white rounded-2xl px-4 py-3 max-w-3xl">
            <p className="text-sm">{message.content || ''}</p>
            <p className="text-xs text-primary-200 mt-1">{message.timestamp || ''}</p>
          </div>
        </div>
      );
    }

    return (
      <div key={message.id} className="flex justify-start mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 w-full shadow-sm">
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <span className="text-sm text-gray-500">{message.timestamp}</span>
          </div>
          
          {message.error ? (
            <div className="text-red-600">{message.content || 'An error occurred'}</div>
          ) : (
            <div className="text-gray-800">
              <div className="prose prose-sm max-w-none">
                {(() => {
                  try {
                    return (
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {message.content || ''}
                      </ReactMarkdown>
                    );
                  } catch (markdownError) {
                    console.error('Error rendering markdown:', markdownError);
                    return (
                      <div className="whitespace-pre-wrap">
                        {message.content || ''}
                      </div>
                    );
                  }
                })()}
              </div>
              {message.reasoning && (
                <details className="mt-4 border border-gray-200 rounded-lg">
                  <summary className="px-4 py-2 cursor-pointer text-sm font-semibold text-gray-700 bg-gray-50 rounded-t-lg hover:bg-gray-100 select-none">
                    Reasoning
                  </summary>
                  <div className="px-4 py-3 text-sm text-gray-600 leading-relaxed">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {message.reasoning}
                    </ReactMarkdown>
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Collapsible Left Sidebar - Chat History */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-16'} bg-white border-r border-gray-200 flex flex-col h-screen flex-shrink-0 transition-all duration-300 ease-in-out`}>
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          {sidebarOpen ? (
            <>
              <h2 className="text-lg font-semibold text-gray-900">Chat History</h2>
              <div className="flex items-center space-x-2">
                {selectedQueryId && (
                  <button
                    onClick={() => setSelectedQueryId(null)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded transition-colors duration-200"
                    title="Show all queries"
                  >
                    Show All
                  </button>
                )}
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700 transition-colors duration-200 mx-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
          {chatHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {sidebarOpen ? <p>No chat history yet</p> : <p className="text-xs">No history</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {Array.isArray(chatHistory) ? chatHistory.map((message, index) => {
                if (!message || typeof message !== 'object') {
                  console.warn(`Invalid message at index ${index}:`, message);
                  return null;
                }
                if (message.type === 'user') {
                  const isSelected = selectedQueryId === message.id;
                  return (
                    <button
                      key={message.id}
                      onClick={() => handleChatHistoryClick(message.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors duration-200 border ${
                        isSelected 
                          ? 'bg-primary-50 border-primary-300 text-primary-900' 
                          : 'border-gray-200 hover:bg-gray-50'
                      } ${!sidebarOpen ? 'text-center' : ''}`}
                    >
                      {sidebarOpen ? (
                        <>
                          <p className={`text-sm font-medium truncate ${
                            isSelected ? 'text-primary-900' : 'text-gray-900'
                          }`}>
                            {(() => {
                              const content = message.content || '';
                              const contentStr = typeof content === 'string' ? content : String(content);
                              return contentStr.length > 50 ? `${contentStr.substring(0, 50)}...` : contentStr;
                            })()}
                          </p>
                          <p className={`text-xs mt-1 ${
                            isSelected ? 'text-primary-600' : 'text-gray-500'
                          }`}>{message.timestamp}</p>
                        </>
                      ) : (
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mx-auto ${
                          isSelected ? 'bg-primary-200' : 'bg-primary-100'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            isSelected ? 'text-primary-700' : 'text-primary-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                }
                return null;
              }) : null}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0 sticky top-0 z-[2000]">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Coverage Agent</h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* User ID input */}
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={userIdDraft}
                  onChange={(e) => setUserIdDraft(e.target.value)}
                  placeholder="Enter User ID"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  style={{ width: '220px' }}
                />
                <button
                  onClick={handleSaveUserId}
                  className="px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors duration-200"
                >
                  Save
                </button>
                {userId && (
                  <span className="text-xs text-gray-500 ml-1">Using: {userId}</span>
                )}
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
                <span>Back</span>
              </button>
              <button
                onClick={handleNewQuery}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>New Chat</span>
              </button>
              {chatHistory.length > 0 && (
                <button
                  onClick={handleClearHistory}
                  className="px-4 py-3 bg-red-100 hover:bg-red-200 text-red-700 font-medium rounded-lg transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Clear History</span>
                </button>
              )}
              
            </div>
          </div>
        </header>

        {/* Single Scrollable Content Area - Chat + Tabs Together */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full max-w-5xl mx-auto px-6 py-4">
            {/* Chat Messages */}
            {(!userId || !userId.trim()) ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86A2.07 2.07 0 0021 16.93L13.93 4.07a2.07 2.07 0 00-3.86 0L3 16.93A2.07 2.07 0 005.07 19z" />
                  </svg>
                  <span className="text-sm text-yellow-800">Please enter and save a User ID to start chatting.</span>
                </div>
              </div>
            ) : chatHistory.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Self Service Analytics</h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  Ask questions about your data in natural language. I'll help you explore, analyze, and visualize your data.
                </p>

                <div className="max-w-4xl mx-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Try these sample queries:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {sampleQueries.map((sampleQuery, index) => (
                      <button
                        key={index}
                        onClick={() => handleSampleQuery(sampleQuery)}
                        className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200"
                      >
                        <p className="text-gray-800 font-medium">{sampleQuery}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Messages */}
                {chatHistory
                  .filter(msg => msg && typeof msg === 'object' && msg.id) // Filter out invalid messages
                  .map((message, index) => {
                    try {
                      return renderMessage(message);
                    } catch (renderError) {
                      console.error(`Error rendering message at index ${index}:`, renderError, message);
                      // Return a fallback error message instead of crashing
                      return (
                        <div key={message?.id || index} className="flex justify-start mb-6">
                          <div className="bg-white border border-red-200 rounded-2xl px-4 py-3 w-full shadow-sm">
                            <div className="text-red-600">
                              Error displaying message. Please refresh the page.
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                
                {/* Loading State — SSE stepper */}
                {isLoading && (
                  <div className="flex justify-start mb-6">
                    <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 shadow-sm">
                      <AnalysisStepper
                        steps={loadingSteps}
                        activeId={loadingActiveId}
                        statusMessage={loadingMessage}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area - Fixed at Bottom */}
        <div className="border-t border-gray-200 bg-white px-6 py-4 flex-shrink-0">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleQuerySubmit} className="flex space-x-4">
              <div className="flex-1">
                <textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask a question about your data..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="1"
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleQuerySubmit(e);
                    }
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || !query.trim()}
                className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-2xl font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Send</span>
                  </>
                )}
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryPage;
